import { Filters, SearchMetadata } from "@nerdjs/nerd-core";
import {
  DeleteAction,
  GetAction,
  PostAction,
  PutAction,
} from "@nerdjs/nerd-network";
import { AnyAction, createAction, Dispatch, nanoid } from "@reduxjs/toolkit";
import { Project, Project_Entity } from "../../entities/project";

export const setStagedProject = createAction(
  "setStagedProject",
  function prepare(stagedProject?: Project_Entity) {
    return {
      payload: {
        stagedProject,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setProjectFormOpen = createAction(
  "setProjectFormOpen",
  function prepare(open: boolean) {
    return {
      payload: {
        open,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setProjectLoading = createAction(
  "setProjectLoading",
  function prepare(loading: boolean) {
    return {
      payload: {
        loading,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setProjectSearchOpen = createAction(
  "setProjectSearchOpen",
  function prepare(searchOpen: boolean) {
    return {
      payload: {
        searchOpen,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setProjectFilters = createAction(
  "setProjectFilters",
  function prepare(filters?: Filters) {
    return {
      payload: {
        filters,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const updateStagedProject = createAction(
  "updateStagedProject",
  function prepare(stagedProject?: Partial<Project_Entity>) {
    return {
      payload: {
        stagedProject,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setSelectedProjectID = createAction(
  "setSelectedProjectID",
  function prepare(projectID?: number) {
    return {
      payload: {
        projectID,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getProjects = createAction(
  "kronos/project/getProjects",
  function prepare() {
    const request = new GetAction(`/kronos/projects`);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const projects = Project.fromList(json);
      dispatch(getProjectsSuccess(projects));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getProjectsSuccess = createAction(
  "kronos/project/getProjectSuccess",
  function prepare(projects: Project[]) {
    return {
      payload: {
        projects,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getProjectsByFilter = createAction(
  "kronos/project/getProjectsByFilter",
  function prepare(query: { [key: string]: unknown }) {
    const request = new GetAction(`/kronos/projects`, query);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const projects = Project.fromList(json);
      dispatch(getProjectsByFilterSuccess(projects));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getProjectsByFilterSuccess = createAction(
  "kronos/project/getProjectsByFilterSuccess",
  function prepare(projects: Project[]) {
    return {
      payload: {
        projects,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getProjectById = createAction(
  "kronos/project/getById",
  function prepare(id: number) {
    const request = new GetAction(`/kronos/projects/${id}`);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const project = new Project(json);
      dispatch(getProjectByIdSuccess(project));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getProjectByIdSuccess = createAction(
  "kronos/project/getProjectByIdSuccess",
  function prepare(project?: Project) {
    return {
      payload: {
        project,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const deleteProject = createAction(
  "kronos/removeProjectById",
  function prepare(id: number, successAction?: AnyAction) {
    const request = new DeleteAction(`/kronos/projects/${id}`);

    if (successAction)
      request.decodeResponse = (dispatch: Dispatch) => {
        dispatch(deleteProjectSuccess(id));
      };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const deleteProjectSuccess = createAction(
  "kronos/projects/deleteProjectSuccess",
  function prepare(projectID: number) {
    return {
      payload: {
        projectID,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const updateProject = createAction(
  "kronos/projects/updateProject",
  function prepare(id: number, body: Partial<Project>) {
    const request = new PutAction(`/kronos/projects/${id}`, body);

    request.decodeResponse = (dispatch: Dispatch) => {
      dispatch(updateProjectSuccess(true));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const updateProjectSuccess = createAction(
  "kronos/projects/updateProjectSuccess",
  function prepare(success: boolean) {
    return {
      payload: {
        success,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const createProject = createAction(
  "kronos/projects/createProject",
  function prepare(body: Partial<Project>) {
    const request = new PostAction(`/kronos/projects`, body);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const project = new Project(json);
      dispatch(createProjectSuccess(project));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const createProjectSuccess = createAction(
  "kronos/projects/createProjectSuccess",
  function prepare(project: Project) {
    return {
      payload: {
        project,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getProjectsSearchMetadata = createAction(
  "kronos/getProjectsSearchMetadata",
  function prepare() {
    const request = new GetAction(`/kronos/projects/searchMetadata`);

    request.decodeResponse = (dispatch: Dispatch, json: SearchMetadata) => {
      dispatch(getProjectsSearchMetadataSuccess(json));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getProjectsSearchMetadataSuccess = createAction(
  "kronos/getProjectsSearchMetadataSuccess",
  function prepare(projectsSearchMetadata: SearchMetadata) {
    return {
      payload: {
        projectsSearchMetadata,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);
